import { log, error } from '../app/logger.js';
import { Weather } from '../data/weather.js';
import { pipeUI } from '../ui/pipe_ui.js';
import { animateUILoading } from '../ui/ui_loader.js';
import { runInBackground } from '../ui/ui_queueable.js';
import { HTMLControl } from './html_control.js';

class WeatherControl extends HTMLControl {
  static selectors = {
    List: 'ul',
  };

  static get elements() {
    return {
      listElement: this.selectors.List,
    };
  }

  static get markup() {
    return `
      <div class='loading-box'>
        <div class='loading-bar40wide'></div>
        <div class='loading-bar80 indent'></div>
        <div class='loading-bar80 indent'></div>
        <div class='loading-bar30'></div>
        <div class='loading-bar50'></div>
      </div>
      <ul class='content-box'></ul>
      <!-- <a href='https://'>Powered by</a> -->
    `;
  }

  set({ loc, startDate, duration }) {
    this.#loc = loc;
    this.#startDate = startDate;
    this.#duration = duration;

    if (this.isInitialized) {
      this.#set();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#loc) {
      this.#set();
    }
  }

  disconnectedCallback() {
    this.clear();
    super.disconnectedCallback();
  }

  clear() {
    this.#pipe?.unpipe();
    this.#pipe = null;
  }

  #set() {
    if (
      !this.#loc?.lat ||
      !this.#loc?.lng ||
      !this.#startDate ||
      !(this.#duration > 0)
    ) {
      error(
        `[control:weather] wrong weather request for id: ${
          this.#loc?.id
        } (lat: ${this.#loc?.lat}, lng: ${this.#loc?.lng}), startDate: ${
          this.#startDate
        }, duration: ${this.#duration}`
      );
      this.clear();
      return;
    }

    log(
      `[control:weather] weather request for '${this.#loc.name}':${
        this.#loc.id
      }, start date: ${this.#startDate}, duration: ${this.#duration}`
    );

    if (!this.#weatherPipeable) {
      this.#weatherPipeable = new Weather();
    }

    animateUILoading(
      this,
      runInBackground(() =>
        this.#weatherPipeable.request({
          loc: this.#loc,
          startDate: this.#startDate,
          duration: this.#duration,
        })
      )
    );

    this.listElement.innerHTML = '';

    this.#pipe?.unpipe();
    this.#pipe = pipeUI({
      pipeable: this.#weatherPipeable,
      parentNode: this.listElement,
      uiFunc: v => this.#uiDay(v),
    });
  }

  #uiDay(weather) {
    let fd = new Date(weather.date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });

    let iconUrl = this.#getIconUrl(weather);

    let sunsetTime = weather.sunsetTime
      ? new Date(weather.sunsetTime).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: 'numeric',
          timeZone: 'UTC',
        })
      : '';
    let sunsetMarkup = sunsetTime ? `Sunset at ${sunsetTime}` : '';

    let temperatureLow = weather.temperatureMin;
    let temperatureHigh = weather.temperatureMax;

    let temperatureLowMarkup = Number.isFinite(weather.temperatureMin)
      ? `
      <span class='temperature'>
        Low ${Math.round(temperatureLow)}&#x2103;
      </span>
    `
      : '';
    let temperatureHighMarkup =
      temperatureLow != temperatureHigh
        ? `<span class='temperature'>
            High ${Math.round(temperatureHigh)}&#x2103;
          </span>`
        : '';

    let precip = '';
    let pop = weather.precipProbability
      ? `${weather.precipProbability * 100}% POP`
      : '';
    if (weather.rain) {
      precip = `Rain: ~${weather.rain}mm ${pop}`;
    }
    if (weather.snow) {
      precip = `Snow: ~${weather.snow}mm ${pop}`;
    }

    return `<li>
      <div class='header'>
        <em class='date'>${fd}</em>
        <img class='icon' src='${iconUrl}'>
      </div>
      <div class='main-block'>
        <div class='summary'>${weather.summary ?? ''}</div>
        <div>
          ${temperatureLowMarkup}
          ${temperatureHighMarkup}
          ${sunsetMarkup}
        </div>
        <div>${precip}</div>
      </div>
    </li>`;
  }

  #getIconUrl(weather) {
    let iconImageName =
      this.constructor.IconImageMap[weather.icon] ?? 'partly_cloudy_day';
    return `/images/weather_icons.svg#${iconImageName}`;
  }

  static tagName = 'weather-control';
  static uiname = 'WeatherControl';

  // Icons from https://icons8.com/icon/set/weather/offices.
  static IconImageMap = {
    'thunderstorm': `storm`,
    'drizzle': `light_rain`,
    'rain': `rain`,
    'freezing-rain': `hail`,
    'shower': `rain_cloud`,
    'snow': `snow`,
    'haze': 'haze',
    'squalls': 'windy_weather',
    'tornado': 'tornado',
    'clear': 'sun',
    'few-clouds': `partly_cloudy_day`,
    'scattered-clouds': `clouds`,
    'clouds': `broken_clouds`,
  };

  #loc;
  #startDate;
  #duration;

  #pipe;
  #weatherPipeable;
}

WeatherControl.defineCustomElement();

export { WeatherControl };
